import { replace } from 'lib/routing/appRouting'
import moment from 'moment'
import { memo, useContext, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import { useIntl } from 'react-intl'

import { fetchCalendarForBedbankOffer, fetchRatesForBedbankOffer } from 'actions/BedbankOfferActions'
import { fetchFlightCalendar } from 'actions/FlightActions'
import { fetchMetasearchDiscount } from 'actions/MetasearchDiscountActions'
import { fetchRelatedTravelItems } from 'actions/OfferActions'
import { trackEvent } from 'analytics/analytics'
import {
  offerPageDurationChangeEvent,
  offerPageGuestSelectionEvent,
  offerPageHotLeadEvent,
} from 'analytics/snowplow/events'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import OfferRecommendations from 'components/Common/OfferRecommendations/OfferRecommendations'
import OfferPageAssistantHost from 'tripPlanner/assistant/surfaces/offerPage/OfferPageAssistantHost'
import { OfferPageAssistantSourceRegistration } from 'tripPlanner/assistant/surfaces/offerPage/OfferPageAssistantSurfaceRegistry'
import {
  createOfferPageSnapshotSelection,
  type BedbankOfferPageSnapshotSource,
} from 'tripPlanner/assistant/surfaces/offerPage/offerPageSnapshotTypes'
import type { PropertyRoomRateViewForAssistant } from 'tripPlanner/assistant/surfaces/offerPage/offerPageContextTypes'
import useOfferPageRoomPackageLoyaltyEarnTotals from 'tripPlanner/assistant/surfaces/offerPage/useOfferPageRoomPackageLoyaltyEarnTotals'
import OfferPageAssistantExpandedContext from 'tripPlanner/assistant/surfaces/offerPage/OfferPageAssistantExpandedContext'
import { isPropertyCollectEnabled as isPropertyCollectEnabledSelector } from 'selectors/offerSelectors'
import { Experiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import BedbankOfferContent from 'components/OfferPage/Bedbank/BedbankOfferContent'
import bedbankMobileFilterMiddleware from 'components/OfferPage/Bedbank/MobileBookingFlow/BedbankMobileFilterMiddleware'
import { getNextFilterWithCapacityCheckInMiddleware } from 'components/OfferPage/utils'
import HotelStructuredData from 'components/Pages/BedbankOfferPage/HotelStructuredData'
import config from 'constants/config'
import {
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_BED_BANK,
} from 'constants/offer'
import { FORCED_SPP_BEDBANK_OFFER_IDS } from 'constants/property'
import {
  metasearchRateContextQueryParams,
  urlHashDatePicker,
} from 'constants/url'
import { MetaOffersDiscountProvider } from 'contexts/MetaOffersDiscountContext'
import OfferPageStateProvider from 'contexts/OfferPage/offerPageStateProvider'
import CurrencyContext from 'contexts/currencyContext'
import type { OfferPageDispatchAction, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useMetasearchSourceTransition from 'hooks/OfferPage/useMetasearchSourceTransition'
import useOfferPageReducer from 'hooks/OfferPage/useOfferPageReducer'
import { useSaveHotLead } from 'hooks/Recommendation/useSaveHotLead'
import { shallowEqual, useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useMetaOffersDiscount } from 'hooks/useMetaOffersDiscount'
import useMountedEffect from 'hooks/useMountedEffect'
import useOfferList from 'hooks/Offers/useOfferList'
import useMetasearchPriceId from 'hooks/useMetasearchPriceId'
import useMetasearchRuleId from 'hooks/useMetasearchRuleId'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { getDisplayTotalPrice, getPromotionDefaultPeriod } from 'lib/hotels/bedbankUtils'
import useBedbankMetasearchDiscountBasePrice from 'hooks/useBedbankMetasearchDiscountBasePrice'
import { without } from 'lib/array/arrayUtils'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import {
  getExperimentEnabledDecision,
  getOptimizelyExperimentVariation,
} from 'lib/optimizely/optimizelyUtils'
import type { ReducerMiddleware } from 'lib/redux/reducerUtils'
import {
  doesOfferPageSearchContextMatch,
  encodeSearchParams,
} from 'lib/search/searchUtils'
import { serialiseOccupancy } from 'lib/search/occupancy'
import { getDefaultAirport, selectIsBedbankFlightBookingEnabled } from 'selectors/flightsSelectors'
import { getPromotionMinLos } from 'selectors/offerPage/bedbankOfferPageSelectors'
import { getInitialBedbankOfferPageState } from 'selectors/offerPage/initialOfferPageStateSelectors'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import ImmersiveSaveButtonPortal from 'tripPlanner/components/ImmersiveHeader/ImmersiveSaveButtonPortal'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { useSyncBedbankTripItem } from 'tripPlanner/hooks/bookmarks/useSyncBedbankTripItem'
import usePrefetchFlightsFromOffer from '../hooks/prefetchFlights/usePrefetchFlightsFromOffer'
import useBedbankPackageViews from 'hooks/useBedbankPackageViews'

const mobileVariantMiddlewares: Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> = [bedbankMobileFilterMiddleware]
const defaultMiddlewares: Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> = [getNextFilterWithCapacityCheckInMiddleware]
const EMPTY_HOTEL_PACKAGE_VIEWS: Array<App.OfferPackageOptionView> = []
const EMPTY_BEDBANK_PACKAGE_VIEWS: Array<App.BedbankOfferPackageView> = []
const EMPTY_PROPERTY_ROOM_RATE_VIEWS: Array<PropertyRoomRateViewForAssistant> = []
const RECOMMENDATION_OFFER_TYPES: Array<App.OfferType> = [
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
]

interface Props {
  offerId: string
  offer: App.BedbankOffer
  footerElement: HTMLElement | null
}

interface BedbankOfferAssistantSourceRegistrationProps {
  offer: App.BedbankOffer
  offerPageState: OfferPageState
  regionCode: string
  flightsEnabled: boolean
}

function BedbankOfferAssistantSourceRegistration({
  offer,
  offerPageState,
  regionCode,
  flightsEnabled,
}: BedbankOfferAssistantSourceRegistrationProps) {
  const currencyCode = useContext(CurrencyContext)
  const [packageViews] = useBedbankPackageViews(offer)
  const isPropertyCollectEnabled = useAppSelector(isPropertyCollectEnabledSelector)
  // Loyalty-earn totals require a real API call (`useLoyaltyPointsEarnCalculator`) -- only compute
  // them once the assistant is actually expanded, rather than firing on every page load.
  const isAssistantExpanded = useContext(OfferPageAssistantExpandedContext)
  const { totalsByGroupKey: roomPackageLoyaltyEarnTotals, status: loyaltyStatus } = useOfferPageRoomPackageLoyaltyEarnTotals({
    offerPageState,
    currentRegionCode: regionCode,
    hotelPackageViews: EMPTY_HOTEL_PACKAGE_VIEWS,
    bedbankPackageViews: isAssistantExpanded ? packageViews : EMPTY_BEDBANK_PACKAGE_VIEWS,
    propertyRoomRateViews: EMPTY_PROPERTY_ROOM_RATE_VIEWS,
    propertyIsUltraLux: false,
  })
  const source = useMemo<BedbankOfferPageSnapshotSource>(() => ({
    key: `offer:${offer.id}`,
    variant: 'bedbank',
    selection: createOfferPageSnapshotSelection(offerPageState),
    priceFormatContext: {
      currencyCode,
      regionCode,
    },
    offer,
    packageViews,
    roomPackageLoyaltyEarnTotals,
    loyaltyStatus,
    isPropertyCollectEnabled,
    flightsEnabled,
  }), [currencyCode, flightsEnabled, isPropertyCollectEnabled, offer, offerPageState, packageViews, regionCode, roomPackageLoyaltyEarnTotals, loyaltyStatus])

  return <OfferPageAssistantSourceRegistration source={source} />
}

function BedbankOfferContainer(props: Props) {
  const intl = useIntl()
  const {
    offerId,
    offer,
    footerElement,
  } = props

  const dispatch = useAppDispatch()
  const location = useLocation()
  const [middlewares, setMiddlewares] = useState<Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> | undefined>(undefined)
  const [
    regionCode,
    promotionMinLos,
    defaults,
    defaultAirport,
    isBedbankFlightBookingEnabled,
    isSpoofed,
    routerLocation,
  ] = useAppSelector(state => [
    state.geo.currentRegionCode,
    getPromotionMinLos(offer),
    getInitialBedbankOfferPageState(state, offer),
    getDefaultAirport(state),
    selectIsBedbankFlightBookingEnabled(state),
    state.auth.account.isSpoofed,
    state.router.location,
  ], shallowEqual)

  const isMobileScreen = useScreenSizeOnly('mobile')

  /************************************************************************************************/
  /* STOP! Do not lift `OfferPageState` into `<BedbankOfferPage/>`. Much like how `<OfferPage/>`  */
  /* defers state management to specific implementations (like `<FlashHotelContainer/>` or        */
  /* `<VillasPageContent/>`), the same applies to bedbank pages (with `<PropertyContainer/>` and  */
  /* `<BedbankOfferContainer/>`). If you think you _need_ to access `OfferPageState` in           */
  /* `<BedbankOfferPage/>`, speak to Xiao first.                                                  */
  /************************************************************************************************/
  const [state, stateDispatch] = useOfferPageReducer({
    ...defaults,
    airport: defaultAirport,
    bedbankOffer: offer,
  }, middlewares)

  const {
    activeFilter,
    checkInDate,
    checkOutDate,
    rooms,
    airport,
    duration,
    selectedBedbankRoomId,
  } = state

  // Format occupancy for meta offers discount
  const occupancyString = rooms.map(generateOccupancyStringByRoom).join(',')

  useSyncBedbankTripItem(state, offer)

  const hasTransitionedFromMetasearch = useMetasearchSourceTransition()
  const discountToken = useMetasearchPriceId()
  const rateRuleId = useMetasearchRuleId()

  const { packages: metaOffersDiscountData, fetching: metaOffersDiscountLoading } = useMetaOffersDiscount({
    offerId,
    region: regionCode,
    checkIn: checkInDate || '',
    checkOut: checkOutDate || '',
    occupancy: occupancyString,
  })

  /**
   * 1. go to a YMAL recommendation
   * 2. Refresh page (clears redux)
   * 3. Go back
   * 4. FETCH_BEDBANK_OFFER action fires, but reducer already computed its initialState where bedbankoffer was initially undefined
   * 5. Fetch done -> loading indicator disappears, component crashes
   *
   * This useEffect is a workaround that.
   */
  useEffect(() => {
    if (offer) {
      stateDispatch({ type: OfferPageActions.setBedBankOffer, bedbankOffer: offer })
      if (defaults.duration) {
        stateDispatch({ type: OfferPageActions.setDuration, duration: defaults.duration })
      }
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [offer])

  useEffect(() => {
    if (isMobileScreen) {
      setMiddlewares(mobileVariantMiddlewares)
    } else {
      setMiddlewares(defaultMiddlewares)
    }
  }, [isMobileScreen])

  useEffect(() => {
    if (!activeFilter && location.hash === urlHashDatePicker) {
      dispatch(replace({ ...location, hash: undefined }))
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter])

  useEffect(() => {
    if (location.hash === urlHashDatePicker) {
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash])

  const occupancyNumber = useMemo(() => sumUpOccupancies(rooms), [rooms])
  const flightsEnabled = !!offer?.flights?.flightsEnabled && isBedbankFlightBookingEnabled && occupancyNumber < 10
  usePrefetchFlightsFromOffer({
    offer,
    offerPageState: state,
    flightsEnabled,
  })

  const shouldKeepMetasearchRateContext = useMemo(() => doesOfferPageSearchContextMatch(
    routerLocation.search,
    checkInDate,
    checkOutDate,
    rooms,
  ), [routerLocation.search, checkInDate, checkOutDate, rooms])

  const queryString = useMemo(() =>
    encodeSearchParams({
      urlSearch: routerLocation.search,
      dates: {
        checkIn: checkInDate,
        checkOut: checkOutDate,
      },
      rooms,
      multiRoomMode: state.multiRoomMode,
      flights: state.flights,
      paramsToKeep: shouldKeepMetasearchRateContext ? metasearchRateContextQueryParams : undefined,
    }).toString(), [
    checkInDate,
    checkOutDate,
    rooms,
    routerLocation.search,
    state.multiRoomMode,
    state.flights,
    shouldKeepMetasearchRateContext,
  ])

  const { hotelOnlyRate, discountBasePrice } = useBedbankMetasearchDiscountBasePrice(offer, rooms, checkInDate, checkOutDate)
  const currentCurrency = useAppSelector(state => state.geo.currentCurrency)

  // Property-fees portion of `discountBasePrice`, forwarded so the resolver can base the discount
  // on the inclusive price (price − propertyFees). Property fees are part of the display price, so
  // they must be split exactly like it. We borrow `getDisplayTotalPrice` purely for that splitting
  // logic (e.g. dividing per-person when `displayPricingAsPerPerson` is set) so `propertyFees` ends
  // up in the same scale as `discountBasePrice` — otherwise `price − propertyFees` mixes units.
  const discountPropertyFees = useMemo(() => {
    if (!checkInDate || !hotelOnlyRate) {
      return undefined
    }

    return getDisplayTotalPrice({
      hotelPrice: hotelOnlyRate.totals.propertyFees ?? 0,
      checkIn: checkInDate,
      rooms,
      displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
    }).totals.price
  }, [checkInDate, hotelOnlyRate, offer.displayPricingAsPerPerson, rooms])

  useEffect(() => {
    if (offer) {
      dispatch(fetchRelatedTravelItems(offer, checkInDate, checkOutDate, rooms))
    }
  }, [offer, checkInDate, checkOutDate, rooms, dispatch])

  useEffect(() => {
    if (!activeFilter || hasTransitionedFromMetasearch) {
      if (offer && checkInDate && checkOutDate && rooms?.length) {
        dispatch(fetchRatesForBedbankOffer(offer, rooms, checkInDate, checkOutDate, hasTransitionedFromMetasearch))
      }
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId, checkInDate, checkOutDate, rooms, offer?.id, activeFilter, hasTransitionedFromMetasearch, metaOffersDiscountLoading])

  useEffect(() => {
    if (discountToken && offer?.id && checkInDate && checkOutDate && discountBasePrice && hotelOnlyRate) {
      dispatch(fetchMetasearchDiscount({
        token: discountToken,
        propertyId: offer.id,
        checkIn: checkInDate,
        checkOut: checkOutDate,
        price: discountBasePrice,
        propertyFees: discountPropertyFees,
        occupancy: serialiseOccupancy(rooms),
        currency: currentCurrency,
        mx: hotelOnlyRate.mx,
        rateRuleId,
      }))
    }
  }, [
    checkInDate,
    checkOutDate,
    currentCurrency,
    discountBasePrice,
    discountPropertyFees,
    discountToken,
    dispatch,
    offer?.id,
    rateRuleId,
    rooms,
    hotelOnlyRate,
  ])

  const checkInMoment = useMemo(() => checkInDate ? moment(checkInDate) : undefined, [checkInDate])
  const checkOutMoment = useMemo(() => checkOutDate ? moment(checkOutDate) : undefined, [checkOutDate])
  const selectedDuration = useMemo(() => checkOutMoment?.diff(checkInMoment, 'days'), [checkInMoment, checkOutMoment])
  const hotLead = useSaveHotLead({
    offerId,
    offerType: 'bedbank_hotel',
    checkInDate,
    checkOutDate,
    duration: selectedDuration,
    occupancy: rooms,
  })

  useEffect(() => {
    if (offer && hotLead?.checkIn && hotLead.checkOut) {
      trackEvent(offerPageHotLeadEvent(offer, state))
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [hotLead])

  useMountedEffect(() => {
    if (state.rooms && offer) {
      trackEvent(offerPageGuestSelectionEvent(offer, state))
    }
  }, [state.rooms])

  useMountedEffect(() => {
    if (selectedDuration && offer) {
      trackEvent(offerPageDurationChangeEvent(offer, state))
    }
  }, [selectedDuration])

  useEffect(() => {
    if (offerId && config.BEDBANK_NEW_CALENDAR && duration && promotionMinLos <= duration) {
      dispatch(fetchCalendarForBedbankOffer(offerId, duration, selectedBedbankRoomId))
    }
  }, [offerId, rooms, duration, promotionMinLos, selectedBedbankRoomId, dispatch])

  const hasPromotions = (offer?.promotions ?? []).length > 0

  useEffect(() => {
    if (offer?.flights.airportCode && flightsEnabled && airport && duration && promotionMinLos <= duration && config.BEDBANK_NEW_CALENDAR) {
      dispatch(fetchFlightCalendar({
        ...getPromotionDefaultPeriod(),
        origin: airport.code,
        destination: offer.flights.airportCode,
        nights: duration,
      }))
    }
  }, [offer, airport, flightsEnabled, promotionMinLos, duration, dispatch])

  useEffect(() => {
    dispatch(replace({ ...routerLocation, search: queryString }))
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString])

  const showRecommendations = !!offer && !isSpoofed && !isMobileScreen

  const offerListFilters = useMemo<App.OfferListFilters>(() => ({
    propertyId: `bedbank:${offerId}`,
    searchNearby: true,
    checkIn: checkInDate,
    checkOut: checkOutDate,
    rooms,
    lere: 'graph',
    baseOfferId: offerId,
  }), [offerId, checkInDate, checkOutDate, rooms])

  const MINIMUM_OFFERS_FOR_SIMILAR_EXPERIMENT = 3
  const recommendationOfferList = useOfferList(
    { ...offerListFilters, offerTypes: RECOMMENDATION_OFFER_TYPES },
    { disabled: !showRecommendations },
  )

  const hasEnoughRecommendations = useMemo(() => {
    return without(recommendationOfferList.offerIds, offerId).length >= MINIMUM_OFFERS_FOR_SIMILAR_EXPERIMENT
  }, [recommendationOfferList.offerIds, offerId])

  const canShowRecommendations = showRecommendations && hasEnoughRecommendations

  // ------------- Control for SPP for bedbank properties with promotions A/B test -------------------

  const isForcedSpp = FORCED_SPP_BEDBANK_OFFER_IDS.has(offerId)
  // Technically, this'll also be `true` if we're not enrolled altogether, but that's fine, as we
  // wouldn't be able to activate an experiment in which we're not enrolled.
  const isControlSppBedbankPromosVariation = useAppSelector(state => !getExperimentEnabledDecision(
    getOptimizelyExperimentVariation(state, Experiments.sppBedbankPromos.id),
  ))
  useExperiment(Experiments.sppBedbankPromos, {
    canActivate: hasPromotions && !isForcedSpp && isControlSppBedbankPromosVariation,
  })

  // ------------- END of control for SPP for bedbank properties with promotions A/B test -------------------

  return (
    <MetaOffersDiscountProvider offerId={offerId} packages={metaOffersDiscountData || []} loading={metaOffersDiscountLoading}>
      <OfferPageStateProvider state={state} dispatch={stateDispatch}>
        <OfferPageAssistantHost offerPageState={state}>
          <BedbankOfferAssistantSourceRegistration
            offer={offer}
            offerPageState={state}
            regionCode={regionCode}
            flightsEnabled={flightsEnabled}
          />
          <HotelStructuredData offer={offer} />
          <TripGuard>
            <ImmersiveSaveButtonPortal>
              <BookmarkButton offer={offer} />
            </ImmersiveSaveButtonPortal>
          </TripGuard>
          <BedbankOfferContent
            flightsEnabled={flightsEnabled}
          />
          {footerElement && showRecommendations && (hasPromotions || canShowRecommendations) && createPortal(
            <ErrorBoundary fallback={null}>
              <OfferRecommendations
                title={intl.formatMessage(
                  {
                    id: '9VU-fbcqdK87hNFh0tLz',
                    defaultMessage: 'Similar to {hotelName}',
                    description: 'Title for similar offers section on the offer listing or detail page',
                  },
                  { hotelName: offer.property.name || 'this' },
                )}
                offerId={offerId}
                filters={offerListFilters}
                overrideOfferList={recommendationOfferList}
                trackingDescription="Offer LE"
                enableDateCheckingForPriceDisplay
              />
            </ErrorBoundary>,
            footerElement,
          )}
        </OfferPageAssistantHost>
      </OfferPageStateProvider>
    </MetaOffersDiscountProvider>
  )
}

export default memo(BedbankOfferContainer)
